import QuoteStore from '../utils/quoteStore.js';
import { PRODUCT_QUOTE_CONFIG } from '../config/config.js';

/**
 * @class ProductQuoteManager
 * @description Manages the product quote functionality for individual product pages
 */
class ProductQuoteManager {
  /**
   * Creates an instance of ProductQuoteManager
   * @constructor
   */
  constructor() {
    this.initialize();
  }

  /**
   * Initializes the quote manager and sets up event listeners
   * @private
   */
  initialize() {
    document.body.addEventListener('click', this.handleQuoteButtonClick.bind(this));
  }

  /**
   * Handles click events on quote buttons
   * @param {Event} event - The click event object
   * @private
   */
  handleQuoteButtonClick(event) {
    const quoteButton = event.target.closest(PRODUCT_QUOTE_CONFIG.QUOTE_BUTTON_SELECTOR);
    if (!quoteButton) return;
    
    event.preventDefault();

    const productData = this.getProductData(quoteButton);
    if (productData) {
      this.addProductToQuote(productData);
    }
  }

  /**
   * Gets product data based on the context
   * @param {HTMLElement} button - The quote button element
   * @returns {Object|null} The product data or null if parsing fails
   * @private
   */
  getProductData(button) {
    try {
      return button.dataset.context === PRODUCT_QUOTE_CONFIG.PRODUCT_CONTEXT
        ? this.getProductPageData(button)
        : this.getCollectionProductData(button);
    } catch (error) {
      console.error(`${PRODUCT_QUOTE_CONFIG.LOG_PREFIX} error getting product data:`, error);
      return null;
    }
  }

  /**
   * Gets product data from the product page form
   * @param {HTMLElement} button - The quote button element
   * @returns {Object} The product data
   * @private
   */
  getProductPageData(button) {
    const formEl = button.closest(PRODUCT_QUOTE_CONFIG.PRODUCT_FORM_SELECTOR);
    const shopifyProduct = JSON.parse(formEl.dataset.product);
    
    const variantId = this.getSelectedVariantId(formEl);
    const shopifyVariant = shopifyProduct.variants.find(v => v.id === variantId);
    
    const variantOptions = this.getSelectedVariantOptions(formEl);
    const imageUrl = this.getProductImage(formEl, shopifyProduct);
    const quantity = this.getSelectedQuantity(formEl);

    return {
      title: shopifyProduct.title,
      handle: shopifyProduct.handle,
      price: shopifyVariant.price / 100,
      qty: quantity,
      variants: [{
        id: shopifyVariant.id,
        title: shopifyVariant.title,
        options: variantOptions,
        available: shopifyVariant.available
      }],
      image: imageUrl
    };
  }

  /**
   * Gets the selected variant ID from the form
   * @param {HTMLElement} form - The product form element
   * @returns {number} The selected variant ID
   * @private
   */
  getSelectedVariantId(form) {
    const selector = form.querySelector(PRODUCT_QUOTE_CONFIG.SELECTORS.VARIANT_ID);
    return parseInt(selector.value, 10);
  }

  /**
   * Gets the selected variant options from the form
   * @param {HTMLElement} form - The product form element
   * @returns {Array<string>} The selected variant options
   * @private
   */
  getSelectedVariantOptions(form) {
    return Array.from(
      form.querySelectorAll(PRODUCT_QUOTE_CONFIG.SELECTORS.SWATCH)
    )
    .filter(input => input.checked)
    .map(input => input.value);
  }

  /**
   * Gets the product image URL
   * @param {HTMLElement} form - The product form element
   * @param {Object} product - The Shopify product object
   * @returns {string} The product image URL
   * @private
   */
  getProductImage(form, product) {
    const colorSwatch = form.querySelector(PRODUCT_QUOTE_CONFIG.SELECTORS.COLOR_SWATCH);
    return colorSwatch?.dataset.image || product.featured_image;
  }

  /**
   * Gets the selected quantity from the form
   * @param {HTMLElement} form - The product form element
   * @returns {number} The selected quantity
   * @private
   */
  getSelectedQuantity(form) {
    const qtyInput = form.querySelector(PRODUCT_QUOTE_CONFIG.SELECTORS.QUANTITY);
    return qtyInput ? parseInt(qtyInput.value, 10) : PRODUCT_QUOTE_CONFIG.DEFAULT_QUANTITY;
  }

  /**
   * Gets product data from collection product data attribute
   * @param {HTMLElement} button - The quote button element
   * @returns {Object} The product data
   * @private
   */
  getCollectionProductData(button) {
    return JSON.parse(button.getAttribute('data-product'));
  }

  /**
   * Adds a product to the quote
   * @param {Object} productData - The product data to add
   * @private
   */
  addProductToQuote(productData) {
    const variant = this.findAvailableVariant(productData);
    if (!variant) {
      console.warn(`${PRODUCT_QUOTE_CONFIG.LOG_PREFIX} no variant available`);
      return;
    }

    this.createQuoteItem(productData, variant);
    this.redirectToQuotePage();
  }

  /**
   * Finds an available variant for the product
   * @param {Object} productData - The product data
   * @returns {Object|undefined} The first available variant or the first variant
   * @private
   */
  findAvailableVariant(productData) {
    return (productData.variants || []).find(v => v.available) 
           || productData.variants?.[0];
  }

  /**
   * Creates a quote item in the store
   * @param {Object} productData - The product data
   * @param {Object} variant - The selected variant
   * @private
   */
  createQuoteItem(productData, variant) {
    QuoteStore.addItem({
      variantId: String(variant.id),
      name: productData.title,
      variantTitle: variant.title,
      variantOptions: variant.options || [],
      price: parseFloat(productData.price),
      handle: productData.handle,
      image: productData.image,
      qty: parseInt(productData.qty, 10) || PRODUCT_QUOTE_CONFIG.DEFAULT_QUANTITY
    });
  }

  /**
   * Redirects to the quote page
   * @private
   */
  redirectToQuotePage() {
    window.location.href = PRODUCT_QUOTE_CONFIG.QUOTE_REDIRECT_URL;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ProductQuoteManager();
});